import { Component, type ErrorInfo, type ReactNode } from "react";
import { logAppError } from "@/app/logAppError";
import { useErrorFallbackPresenter } from "@/app/useErrorFallbackPresenter";
import { Button } from "@/shared/ui/form/Button";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  override state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logAppError(error, errorInfo);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <AppErrorFallback />;
    }

    return this.props.children;
  }
}

export const AppErrorFallback: React.FunctionComponent = () => {
  const onReload = useErrorFallbackPresenter();
  return <AppErrorFallbackView onReload={onReload} />;
};

export const AppErrorFallbackView: React.FunctionComponent<{
  onReload: () => void;
}> = ({ onReload }: { onReload: () => void }) => (
  <section
    role="alert"
    aria-labelledby="app-error-heading"
    className="flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground"
  >
    <article className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-border bg-card p-6 text-center">
      <h1 id="app-error-heading" className="text-xl font-semibold">
        エラーが発生しました
      </h1>
      <p>ページを表示できません。再読み込みしてください。</p>
      <Button variant="primary" onClick={onReload}>
        再読み込み
      </Button>
    </article>
  </section>
);
